import { getItem, setItem } from '@alltrails/local-storage/utils';
import type { AdvancedMappingState } from '@/utils/slices/advancedMappingSlice';
import { isToggledMapContentEntry, mapContentEntriesById, type MapContentId } from './mapContentRegistry';
import type { MapContentVisibility } from './mapContentVisibility';

export const mapContentStorageKey = 'advanced-mapping-content';

// Bump when the stored shape changes; a mismatch discards the stored value
const storageVersion = 1;

type StoredMapContent = { v: number } & Partial<AdvancedMappingState>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// The reducer ignores style-backed ids, so a stored one could never be cleared
const isStorableContentId = (id: string) =>
  Object.hasOwn(mapContentEntriesById, id) && !isToggledMapContentEntry(mapContentEntriesById[id as MapContentId]);

const parseStoredVisibility = (contentVisibility: unknown): MapContentVisibility | undefined => {
  if (!isRecord(contentVisibility)) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(contentVisibility).filter(([id, isVisible]) => isStorableContentId(id) && typeof isVisible === 'boolean'));
};

export const readStoredMapContent = (): Partial<AdvancedMappingState> | undefined => {
  const stored = getItem<StoredMapContent>(mapContentStorageKey);
  if (!stored || stored.v !== storageVersion) {
    return undefined;
  }

  const contentVisibility = parseStoredVisibility(stored.contentVisibility);
  const restored = { ...(contentVisibility ? { contentVisibility } : {}) };

  return Object.keys(restored).length > 0 ? restored : undefined;
};

export const writeStoredMapContent = (state: AdvancedMappingState) =>
  setItem<StoredMapContent>(mapContentStorageKey, { ...state, v: storageVersion });
